import React, { createContext, useContext, useState, useEffect } from 'react';

type NotificationContextType = {
  message: string;
  isVisible: boolean;
  showNotification: (message: string) => void;
  hideNotification: () => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  // Load notification state from localStorage on initial render
  useEffect(() => {
    const savedMessage = localStorage.getItem('notification_message');
    const savedVisibility = localStorage.getItem('notification_visible');
    
    if (savedMessage) {
      setMessage(savedMessage);
    }
    
    if (savedVisibility === 'true') {
      setIsVisible(true);
    }
  }, []);

  const showNotification = (message: string) => {
    setMessage(message);
    setIsVisible(true);
    
    // Store in localStorage
    localStorage.setItem('notification_message', message);
    localStorage.setItem('notification_visible', 'true');
  };

  const hideNotification = () => {
    setIsVisible(false);
    
    // Update localStorage
    localStorage.setItem('notification_visible', 'false');
    // Note: We keep the message in localStorage even when hidden,
    // so admins can still see what the previous message was
  };

  return (
    <NotificationContext.Provider
      value={{ message, isVisible, showNotification, hideNotification }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
} 